import { useEffect, useMemo, useRef } from 'react'

import type { ColorSchemeMode } from '@tribeplatform/design-system/types'
import { Form, type FormRef } from '@tribeplatform/react-components/Form'
import { useI18n } from '@tribeplatform/react-components/i18n'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import {
  type UserColorScheme,
  useColorScheme,
} from '@tribeplatform/react-ui-kit/ThemeProvider'

import { ColorSchemeSettingsForm } from './ColorSchemeSettingsForm.js'

export const ColorSchemeSettings = () => {
  const { $t } = useI18n()
  const formRef = useRef<FormRef>()
  const { colorSchemeMode, colorScheme, deviceColorMode, setMode } =
    useColorScheme()
  const defaultValues = useMemo(() => {
    return {
      colorMode: colorScheme === 'auto' ? deviceColorMode : colorSchemeMode,
      syncColorMode: colorScheme === 'auto',
    }
  }, [colorScheme, colorSchemeMode, deviceColorMode])

  const handleUpdate = ({
    colorMode,
    syncColorMode,
  }: {
    colorMode: ColorSchemeMode
    syncColorMode: boolean
  }) => {
    if (syncColorMode) {
      setMode('auto')
    } else {
      setMode(colorMode as UserColorScheme)
    }
  }

  useEffect(() => {
    if (formRef.current?.methods) {
      formRef.current.methods.reset(defaultValues)
    }
  }, [defaultValues])

  if (!deviceColorMode) {
    return (
      <Card>
        <Card.Header>
          <div className="h-6 bg-skeleton rounded-base w-1/4 animate-pulse" />
        </Card.Header>
        <Card.Content>
          <div className="animate-pulse">
            <div className="space-y-2">
              <div className="h-4 bg-skeleton rounded-full" />
              <div className="h-4 bg-skeleton rounded-full w-5/6" />
            </div>
          </div>
        </Card.Content>
      </Card>
    )
  }

  return (
    <Card>
      <Card.Header
        title={$t({
          id: 'Generics.DarkMode',
          defaultMessage: 'Dark mode',
        })}
      />
      <Card.Content>
        <Form
          ref={formRef}
          defaultValues={defaultValues}
          onSubmit={handleUpdate}
        >
          <ColorSchemeSettingsForm />
        </Form>
      </Card.Content>
    </Card>
  )
}
